'use client';

import { useCallback } from 'react';
import { useGardenPlannerStore } from '@shared/store/useGardenPlannerStore';
import { useSetCellSlotsPerCell, useSetBulkCellSlotsPerCell } from '@api/gardens';
import { useGarden } from '@modules/garden-planner/context/garden_planner_context';
import {
  createHandleCellSlotsChange,
  createHandleBulkSaveCellSettings,
  createHandleSaveCellSettings,
  createHandleBulkSaveWithCleanup,
} from '@modules/garden-planner/sidebar/cell_settings_tool/business_logic';
import type { GardenPlannerViews } from '@modules/garden-planner/garden_planner_views';

interface CellSettingsToolContainerProps {
  views: GardenPlannerViews;
}

export function CellSettingsToolContainer({ views }: CellSettingsToolContainerProps) {
  const { CellSettingsToolView } = views;
  const isCellSettingsMode = useGardenPlannerStore((state) => state.isCellSettingsMode);
  const setActiveMode = useGardenPlannerStore((state) => state.setActiveMode);
  const garden = useGarden();
  const selectedCell = useGardenPlannerStore((state) => state.selectedCell);
  const setSelectedCell = useGardenPlannerStore((state) => state.setSelectedCell);
  const slotsPerCell = useGardenPlannerStore((state) => state.slotsPerCell);
  const setSlotsPerCell = useGardenPlannerStore((state) => state.setSlotsPerCell);
  const isBulkEditMode = useGardenPlannerStore((state) => state.isBulkEditMode);
  const setBulkEditMode = useGardenPlannerStore((state) => state.setBulkEditMode);
  const bulkSelectedCells = useGardenPlannerStore((state) => state.bulkSelectedCells);
  const clearBulkSelectedCells = useGardenPlannerStore((state) => state.clearBulkSelectedCells);

  const setCellSlotsPerCell = useSetCellSlotsPerCell();
  const setBulkCellSlotsPerCell = useSetBulkCellSlotsPerCell();

  const getSlotsForCell = useCallback(
    (row: number, col: number) => {
      const entry = (garden.cellSettings || []).find((c) => c.row === row && c.col === col);
      return entry?.slotsPerCell ?? 1;
    },
    [garden.cellSettings]
  );

  const handleCellSlotsChange = createHandleCellSlotsChange(garden, setCellSlotsPerCell);
  const handleSaveCellSettings = createHandleSaveCellSettings(
    selectedCell,
    slotsPerCell,
    handleCellSlotsChange
  );
  const handleBulkSave = createHandleBulkSaveCellSettings(
    garden,
    bulkSelectedCells,
    setBulkCellSlotsPerCell
  );
  const handleBulkSaveWithCleanup = createHandleBulkSaveWithCleanup(
    handleBulkSave,
    slotsPerCell,
    clearBulkSelectedCells
  );

  return (
    <CellSettingsToolView
      isActive={isCellSettingsMode}
      selectedCell={selectedCell}
      slotsPerCell={slotsPerCell}
      getSlotsForCell={getSlotsForCell}
      onToggle={() => setActiveMode(isCellSettingsMode ? null : 'cellSettings')}
      onSelectCellClear={() => setSelectedCell(null)}
      onSlotsPerCellChange={setSlotsPerCell}
      onSaveCellSettings={handleSaveCellSettings}
      isBulkEditMode={isBulkEditMode}
      onToggleBulkMode={(enabled) => setBulkEditMode(enabled)}
      bulkSelectionCount={bulkSelectedCells.length}
      onBulkSave={handleBulkSaveWithCleanup}
    />
  );
}
